// src/backup/index.js — екран „Резервни копия" (само администратор).
//
// ЗАЩО го има: досега проверката „има ли изобщо копие и от кога" и
// прехвърлянето на данни към тестовия сървър се правеха с команди в терминала
// на сървъра. Собственикът е счетоводител — терминалът не е място за него.
// Целият този екран е един прочит наум: кога е последното копие, включени ли
// са защитите, кои архиви ги има, и два бутона за ръчна работа.
//
// Данните идват от:
//   GET  /api/backup/list            — архивите + кога е последното успешно копие
//   POST /api/backup/run             — прави копие веднага
//   GET  /api/backup/download        — сваля JSON дъмп (същото като в „Потребители")
//   GET  /api/backup/restore/allowed — разрешено ли е възстановяване ТУК
//   POST /api/backup/restore         — замяна на всички данни (тяло: ТЕКСТ)
//   GET  /health/backup-config       — да/не за криптиране, офсайт, имейл
//   GET  /health/ocr                 — работи ли разчитането и коя версия

import { el, clear, field } from '../shared/dom.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { emptyState } from '../shared/ui.js';
import { confirmDialog } from '../shared/modal.js';
import { whenFmt } from '../shared/audit.js';
import { state } from '../shared/state.js';

// Копие по-старо от денонощие значи, че автоматиката е спряла (интервалът по
// подразбиране е 24 ч). Прагът е тук, за да не се търси из кода.
const STALE_HOURS = 24;

// Размерът в байтове не говори нищо на човек — 4823942 е „4,6 MB".
export function humanSize(bytes) {
  // Number(null) е 0 — липсващ размер не бива да се показва като „0 B".
  if (bytes == null || bytes === '') return '—';
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return Math.round(n / 1024) + ' KB';
  return (n / (1024 * 1024)).toFixed(1).replace('.', ',') + ' MB';
}

// „преди 3 часа" / „преди 2 дни" — възрастта е по-разбираема от датата.
export function ageText(hours) {
  const h = Number(hours);
  if (!Number.isFinite(h) || h < 0) return '';
  if (h < 1) return 'преди по-малко от час';
  if (h < 24) { const n = Math.round(h); return 'преди ' + n + (n === 1 ? ' час' : ' часа'); }
  const d = Math.floor(h / 24);
  return 'преди ' + d + (d === 1 ? ' ден' : ' дни');
}

function yesNo(v) { return v ? 'Да' : 'Не'; }

// /health/backup-config живее ИЗВЪН /api, затова api() (който слага „/api"
// отпред) не върши работа тук — вика се направо.
async function loadConfig() {
  const res = await fetch('/health/backup-config', { credentials: 'same-origin' });
  if (!res || !res.ok) throw new Error('Няма достъп до настройките на копията.');
  return res.json();
}

export async function view(page) {
  clear(page);
  const st = state();
  // Проверката е ПРЕДИ всяка заявка: не-админ не бива дори да пита сървъра.
  if (!st.user || st.user.role !== 'admin') {
    page.appendChild(el('div', { class: 'panel' },
      emptyState('▣', 'Резервните копия са достъпни само за администратор.')));
    return;
  }

  const statusPanel = el('div', { class: 'panel' });
  const listPanel = el('div', { class: 'panel', style: 'margin-top:16px' });
  const restorePanel = el('div', { class: 'panel', style: 'margin-top:16px' });
  const ocrPanel = el('div', { class: 'panel ocr-status', style: 'margin-top:16px' });
  page.appendChild(statusPanel);
  page.appendChild(listPanel);
  page.appendChild(restorePanel);
  page.appendChild(ocrPanel);

  await drawStatusAndList(statusPanel, listPanel);
  await drawRestore(restorePanel);
  await drawOcr(ocrPanel);
}

// ---- Разчитане на фактури (OCR) ---------------------------------------------
// Едно място, от което админът вижда дали услугата за разчитане работи и коя
// версия реално върви на сървъра — за да е ясно дали поправка вече е качена
// (Макс, 19.10.2026). Версията е кратък код (commit) като в GitHub.
async function drawOcr(panel) {
  clear(panel);
  panel.appendChild(el('div', { class: 'panel-head' }, el('h2', { text: 'Разчитане на фактури (OCR)' })));
  let o;
  try {
    const res = await fetch('/health/ocr', { credentials: 'same-origin' });
    if (!res || !res.ok) throw new Error('няма достъп');
    o = await res.json();
  } catch (e) {
    panel.appendChild(el('div', { class: 'cal-hint', text: 'Не успях да проверя услугата за разчитане: ' + e.message }));
    return;
  }
  const row = (label, value, tone) => el('div', { style: 'display:flex;gap:8px;align-items:baseline;margin:2px 0' },
    el('span', { style: 'font-weight:600;min-width:150px', text: label }),
    tone ? el('span', { class: 'tag ' + tone, text: value }) : el('span', { text: value }));
  if (!o.enabled) {
    panel.appendChild(row('Състояние', 'Изключено', 'gray'));
    panel.appendChild(el('div', { class: 'cal-hint', text: o.reason || 'Доставките се въвеждат ръчно.' }));
    return;
  }
  if (o.driver !== 'vikonti') {
    panel.appendChild(row('Състояние', 'Работи през Claude', 'green'));
    return;
  }
  if (!o.reachable) {
    panel.appendChild(row('Състояние', 'Не отговаря', 'red'));
    panel.appendChild(el('div', { class: 'cal-hint', text: (o.error || '') + ' Докато не тръгне, доставките се въвеждат ръчно' + (o.fallback ? ' или се разчитат през Claude (платено).' : '.') }));
    return;
  }
  panel.appendChild(row('Състояние', o.ok ? 'Работи' : 'Работи, но без български език', o.ok ? 'green' : 'red'));
  panel.appendChild(row('Версия', o.version === 'unknown' ? 'неизвестна' : o.version));
  if (o.version === 'unknown') {
    panel.appendChild(el('div', { class: 'cal-hint', text: 'Версията не се вижда: в Coolify, в настройките на OCR приложението, включете „Include Source Commit in Build" и го пуснете отново.' }));
  }
  panel.appendChild(row('Резерва към Claude', yesNo(o.ai || !!o.fallback), (o.ai || o.fallback) ? 'green' : 'gray'));
}

// ---- Състояние + списък -----------------------------------------------------

async function drawStatusAndList(statusPanel, listPanel) {
  clear(statusPanel);
  clear(listPanel);

  let data = null, cfgErr = null, cfg = null;
  try { data = await api('/backup/list'); }
  catch (e) { data = { error: e.message }; }
  try { cfg = await loadConfig(); } catch (e) { cfgErr = e.message; }

  const backups = Array.isArray(data && data.backups) ? data.backups.slice() : [];
  // Договорът казва „най-новите първи", но подредбата е важна за човека —
  // затова я налагаме и тук, а не разчитаме сървърът да не сгреши.
  backups.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));

  const last = data && data.last_success_at;
  const age = data && data.age_hours;
  const stale = !last || (Number.isFinite(Number(age)) && Number(age) > STALE_HOURS);

  statusPanel.appendChild(el('div', { class: 'panel-head' },
    el('h2', { text: 'Резервни копия — състояние' }),
    el('button', {
      class: 'btn sm', text: '⟳ Направи копие сега',
      onClick: (e) => runNow(e.currentTarget, statusPanel, listPanel),
    }),
    // Пълното копие (с паролите и таксите) сваля само собственикът —
    // сървърът така или иначе отказва на другите (ownerRequired).
    (state().user && state().user.owner) ? el('button', {
      class: 'btn sm ghost', style: 'margin-left:8px', text: '⤓ Изтегли резервно копие',
      onClick: () => downloadDump(),
    }) : null
  ));

  if (data && data.error) {
    statusPanel.appendChild(el('div', { class: 'cal-hint', text: 'Не успях да прочета копията: ' + data.error }));
  }

  statusPanel.appendChild(el('div', { class: 'cards' },
    // Едно изречение с обикновени думи вместо ISO дата.
    el('div', { class: 'stat' + (stale ? ' warn' : '') },
      el('div', { class: 'label', text: 'Последно успешно копие' }),
      el('div', { class: 'value', text: last ? whenFmt(last) : 'Още няма' }),
      el('div', { class: 'sub' + (stale ? ' alarm' : ''), text: last ? ageText(age) : 'Копие още не е правено.' })),
    el('div', { class: 'stat' },
      el('div', { class: 'label', text: 'Налични архиви' }),
      el('div', { class: 'value', text: String(backups.length) }),
      el('div', { class: 'sub', text: 'на сървъра' }))
  ));

  if (stale) {
    statusPanel.appendChild(el('div', { class: 'backup-stale tag red', style: 'display:block;margin:4px 0 10px;padding:8px 10px;line-height:1.5' },
      last
        ? '⚠ Последното копие е отпреди повече от 24 часа. Автоматичните копия вероятно са спрели — направете копие сега и проверете журнала на приложението.'
        : '⚠ Още няма направено нито едно копие. Натиснете „Направи копие сега".'));
  }

  // Трите защити — само да/не, точно както ги дава сървърът.
  if (cfgErr) {
    statusPanel.appendChild(el('div', { class: 'cal-hint', text: 'Не успях да прочета настройките на копията: ' + cfgErr }));
  } else {
    const c = cfg || {};
    const line = (label, on, why) => el('div', { style: 'display:flex;gap:8px;align-items:baseline;margin:2px 0' },
      el('span', { class: 'tag ' + (on ? 'green' : 'gray'), text: yesNo(on) }),
      el('span', { style: 'font-weight:600', text: label }),
      el('span', { style: 'color:var(--muted);font-size:13px', text: why }));
    statusPanel.appendChild(el('div', { style: 'padding:2px 2px 10px' },
      line('Криптиране на архивите', c.encryption, 'архивът напуска сървъра нечетим'),
      line('Копие извън сървъра', c.offsite, 'качва се и в отделно хранилище'),
      line('Имейл бекъп веднъж седмично', c.email_backup, 'праща се само криптиран')));
  }

  // ---- Списъкът с архивите ----
  listPanel.appendChild(el('div', { class: 'panel-head' }, el('h2', { text: 'Архиви на сървъра' })));
  if (!backups.length) {
    listPanel.appendChild(emptyState('▣',
      'На сървъра още няма запазени архиви. Натиснете „Направи копие сега" горе — копието се записва на сървъра и остава там.'));
    return;
  }

  const tb = el('tbody');
  backups.forEach((b) => {
    tb.appendChild(el('tr', {},
      el('td', { style: 'white-space:nowrap', text: b.name || '—' }),
      el('td', { style: 'white-space:nowrap', text: humanSize(b.size_bytes) }),
      el('td', { style: 'white-space:nowrap', text: whenFmt(b.created_at) }),
      el('td', {}, el('span', { class: 'tag ' + (b.encrypted ? 'green' : 'gray'), text: b.encrypted ? 'Шифрован' : 'Нешифрован' }))
    ));
  });
  listPanel.appendChild(el('table', {}, el('thead', {}, el('tr', {},
    el('th', { scope: 'col', text: 'Име на файла' }),
    el('th', { scope: 'col', text: 'Размер' }),
    el('th', { scope: 'col', text: 'Дата и час' }),
    el('th', { scope: 'col', text: 'Защита' })
  )), tb));
  listPanel.appendChild(el('div', { class: 'cal-hint', text: 'Най-новите са първи. Пазят се последните архиви — по-старите се трият автоматично.' }));
}

// „Направи копие сега" — бутонът се заключва, докато трае заявката, иначе
// нетърпеливото двойно натискане прави две копия.
async function runNow(btn, statusPanel, listPanel) {
  if (btn) { btn.disabled = true; btn.textContent = 'Правя копие…'; }
  try {
    const r = await api('/backup/run', { method: 'POST' });
    toast('Копието е готово: ' + (r && r.name ? r.name : '') + (r && r.size_bytes != null ? ' (' + humanSize(r.size_bytes) + ')' : ''));
  } catch (e) {
    toast(e.message, true);
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = '⟳ Направи копие сега'; }
  }
  await drawStatusAndList(statusPanel, listPanel);
}

// Същият похват като в „Потребители": скрита връзка, за да тръгне свалянето
// със сесийната бисквитка (fetch + blob не носи име на файла безболезнено).
function downloadDump() {
  const a = document.createElement('a');
  a.href = '/api/backup/download';
  document.body.appendChild(a);
  a.click();
  a.remove();
  toast('Започва изтегляне…');
}

// ---- Възстановяване ---------------------------------------------------------

async function drawRestore(panel) {
  clear(panel);
  panel.appendChild(el('div', { class: 'panel-head' }, el('h2', { text: 'Възстановяване от файл' })));

  let info = null;
  try { info = await api('/backup/restore/allowed'); } catch (e) { info = { allowed: false, error: e.message }; }

  if (!info || !info.allowed) {
    // Не е повреда, а нарочна настройка на живия сървър — казва се спокойно.
    panel.appendChild(el('div', { style: 'padding:2px 2px 12px;color:var(--muted);font-size:14px;line-height:1.6' },
      el('div', { text: 'На този сървър възстановяването е изключено НАРОЧНО.' }),
      el('div', { text: 'Замяната на всички данни е опасна операция и затова е разрешена само на тестовия сървър. Така един погрешен файл не може да изтрие живите данни.' }),
      el('div', { text: 'Копие за съхранение навън се взима с бутона „Изтегли резервно копие" горе.' })));
    return;
  }

  const fileInp = el('input', { type: 'file', accept: '.json,application/json' });
  const status = el('div', { class: 'cal-hint', text: 'Изберете файл, свален с „Изтегли резервно копие".' });
  let fileText = '';
  let fileName = '';

  // Файлът се чете В БРАУЗЪРА и се праща като текст (същият похват като вноса
  // на бланката) — сървърът приема text/plain, защото пълният дъмп надхвърля
  // лимита на express.json.
  fileInp.addEventListener('change', () => {
    const f = fileInp.files && fileInp.files[0];
    fileText = ''; fileName = '';
    if (!f) { status.textContent = 'Изберете файл, свален с „Изтегли резервно копие".'; return; }
    const reader = new FileReader();
    reader.onload = () => {
      fileText = String(reader.result || '');
      fileName = f.name;
      status.textContent = 'Файл: ' + f.name + ' (' + humanSize(f.size) + '). Натиснете „Възстанови от този файл".';
    };
    reader.onerror = () => { status.textContent = 'Грешка при четене на файла.'; };
    reader.readAsText(f);
  });

  const btn = el('button', { class: 'btn danger', text: 'Възстанови от този файл', onClick: () => doRestore() });

  panel.appendChild(el('div', { style: 'padding:2px 2px 10px;color:var(--muted);font-size:14px;line-height:1.6' },
    el('div', { text: 'Този сървър е тестов (' + ((info.engine || '') + (info.driver ? ' / ' + info.driver : '') || 'без данни за двигателя') + ') и позволява замяна на данните.' }),
    el('div', { style: 'color:var(--red);font-weight:600', text: 'Възстановяването ЗАМЕНЯ всички данни с тези от файла. Всичко, въведено СЛЕД момента на архива, се губи безвъзвратно.' })));
  panel.appendChild(field('Файл с резервно копие (.json)', fileInp));
  panel.appendChild(status);
  panel.appendChild(el('div', { style: 'margin-top:8px' }, btn));

  async function doRestore() {
    if (!fileText) { toast('Първо изберете файл.', true); return; }
    // Изричен текст на бутона — никаква подразбираща се разрушителна стойност.
    const ok = await confirmDialog(
      'Замяна на ВСИЧКИ данни с файла „' + fileName + '".\n\n' +
      'Сегашните фактури, протоколи, контакти, задачи и бележки се изтриват и на тяхно място идват тези от архива. ' +
      'Всичко, въведено след момента на архива, изчезва безвъзвратно.',
      { okText: 'Да, замени всички данни', danger: true }
    );
    if (!ok) return;
    btn.disabled = true;
    try {
      const r = await api('/backup/restore', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain', 'X-Confirm-Restore': 'REPLACE-ALL-DATA' },
        body: fileText,
      });
      toast('Данните са възстановени' + (r && r.snapshot ? ' (старите са запазени в ' + r.snapshot + ')' : '') + '.');
    } catch (e) {
      toast(e.message, true);
    } finally {
      btn.disabled = false;
    }
  }
}
